import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider,
  Chip,
} from "@mui/material";
import useAuthStore from "../../../store/useAuthStore";

const DeductionDetails = () => {
  const { token } = useAuthStore();
  const [tabIndex, setTabIndex] = useState(0);

  // Deduction tab state
  const [selectedDeduction, setSelectedDeduction] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState([]);
  const [error, setError] = useState("");
  const [allDeductionNames, setAllDeductionNames] = useState([]);
  const [isPaid, setIsPaid] = useState(false);
  const [payLoading, setPayLoading] = useState(false);

  // TDS tab state
  const [accounts, setAccounts] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [tdsLoading, setTdsLoading] = useState(false);
  const [tdsError, setTdsError] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("all");
  const [tdsRows, setTdsRows] = useState([]);
  const [tdsTotals, setTdsTotals] = useState({ expenseTotal: 0, tdsAmount: 0, expenseCount: 0 });

  // Fetch all deduction names from both deduction types and payslips
  useEffect(() => {
    const fetchAllDeductionNames = async () => {
      setLoading(true);
      setError("");
      try {
        const res1 = await fetch("/api/getAllDeductionTypes", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data1 = await res1.json();
        const namesFromTypes = (data1.deductionTypes || []).map((d) => d.name);

        const res2 = await fetch("/api/payslips", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data2 = await res2.json();
        const namesFromPayslips = Array.from(
          new Set(
            (data2.payslips || [])
              .flatMap((p) => (p.deductions || []).map((d) => d.name))
              .filter(Boolean)
          )
        );

        const allNames = Array.from(new Set([...namesFromTypes, ...namesFromPayslips]));
        setAllDeductionNames(allNames.sort((a, b) => a.localeCompare(b)));
      } catch (err) {
        setError("Failed to fetch deduction names.");
      } finally {
        setLoading(false);
      }
    };
    fetchAllDeductionNames();
  }, [token]);

  // Check if already paid for this deduction/month
  useEffect(() => {
    const checkPaidStatus = async () => {
      if (!selectedDeduction || !selectedMonth) {
        setIsPaid(false);
        return;
      }
      try {
        const res = await fetch("/api/getallexpense", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const paid = (data.expenses || []).some(
          (exp) =>
            exp.header === "Direct Expense" &&
            exp.subheader === selectedDeduction &&
            (exp.date?.slice(0, 7) === selectedMonth ||
              exp.date === selectedMonth)
        );
        setIsPaid(paid);
      } catch {
        setIsPaid(false);
      }
    };
    checkPaidStatus();
  }, [selectedDeduction, selectedMonth, token, result]);

  // Fetch TDS base data (accounts and expenses) when switching to TDS tab
  useEffect(() => {
    const fetchTdsBase = async () => {
      if (tabIndex !== 1) return;
      setTdsLoading(true);
      setTdsError("");
      try {
        const [accRes, expRes] = await Promise.all([
          fetch("/api/getAllAccounts", { headers: { Authorization: `Bearer ${token}` } }),
          fetch("/api/getallexpense", { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const accData = await accRes.json();
        const expData = await expRes.json();
        setAccounts(Array.isArray(accData.accounts) ? accData.accounts : []);
        setExpenses(Array.isArray(expData.expenses) ? expData.expenses : []);
      } catch (e) {
        setTdsError("Failed to fetch accounts/expenses.");
      } finally {
        setTdsLoading(false);
      }
    };
    fetchTdsBase();
  }, [tabIndex, token]);

  // Deduction: fetch and compute
  const handleUpdate = async () => {
    if (!selectedDeduction || !selectedMonth) {
      setError("Please select both deduction name and month.");
      return;
    }
    setLoading(true);
    setError("");
    setResult([]);
    try {
      const res = await fetch("/api/payslips", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Failed to fetch payslips");
        setLoading(false);
        return;
      }
      const filteredPayslips = (data.payslips || []).filter((p) => {
        const payslipMonth = new Date(p.endDate).toISOString().slice(0, 7);
        return payslipMonth === selectedMonth && p.status === "Confirmed";
      });

      const details = filteredPayslips
        .map((p) => {
          const deduction = (p.deductions || []).find(
            (d) =>
              d.name &&
              d.name.toLowerCase() === selectedDeduction.toLowerCase()
          );
          return deduction
            ? {
              employee: p.name,
              email: p.email,
              amount: deduction.amount,
            }
            : null;
        })
        .filter(Boolean);

      setResult(details);
      if (details.length === 0) {
        setError("No deductions found for the selected criteria.");
      }
    } catch (err) {
      setError("Failed to fetch deduction details.");
    } finally {
      setLoading(false);
    }
  };

  const totalDeduction = result.reduce((sum, row) => sum + Number(row.amount || 0), 0);

  const handleProceedToPay = async () => {
    setPayLoading(true);
    setError("");
    try {
      const expenseDate = selectedMonth ? `${selectedMonth}-1` : "";
      const expensePayload = {
        header: "Direct Expense",
        subheader: selectedDeduction,
        amount: totalDeduction,
        date: expenseDate,
        description: `Paid deduction of ${selectedDeduction} to organization for ${selectedMonth}`,
      };
      const res = await fetch("/api/addExpense", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(expensePayload),
      });
      const data = await res.json();
      if (res.ok && data.expense) {
        setIsPaid(true);
      } else {
        setError(data.error || "Failed to proceed to pay.");
      }
    } catch (err) {
      setError("Failed to proceed to pay.");
    } finally {
      setPayLoading(false);
    }
  };

  // TDS helpers
  const parseDate = (value) => {
    if (!value) return null;
    const normalized = typeof value === "string" && value.match(/^\d{4}-\d{1,2}-\d{1,2}$/)
      ? value
      : String(value);
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
  };

  const isWithinRange = useCallback((dateStr) => {
    if (!startDate && !endDate) return true;
    const d = parseDate(dateStr);
    if (!d) return false;
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    if (start && d < start) return false;
    if (end) {
      const endDay = new Date(end);
      endDay.setHours(23, 59, 59, 999);
      if (d > endDay) return false;
    }
    return true;
  }, [startDate, endDate]);

  const taxableAccounts = useMemo(
    () => (accounts || [])
      .filter((a) => Number(a.taxRate) > 0)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [accounts]
  );

  const handleTdsUpdate = () => {
    const normalize = (v) => (v == null ? "" : String(v).trim().toLowerCase());

    const filteredAccounts = selectedAccountId === "all"
      ? taxableAccounts
      : taxableAccounts.filter((a) => String(a._id) === String(selectedAccountId));

    // Build quick lookup by normalized patterns: `${code}-${name}` and `name`
    const namePatterns = new Map(); // key: normalized subheader token -> account
    filteredAccounts.forEach((a) => {
      const normName = normalize(a.name);
      const codeDashName = normalize(`${a.code}-${a.name}`);
      const codeDashSpaceName = normalize(`${a.code} - ${a.name}`);
      namePatterns.set(codeDashName, a);
      namePatterns.set(codeDashSpaceName, a);
      namePatterns.set(normName, a);
      // also accept subheader ending with `-${name}`
      namePatterns.set(`-${normName}`, a);
    });

    const relevantExpenses = (expenses || []).filter((exp) => {
      if (!isWithinRange(exp.date)) return false;
      const sub = normalize(exp.subheader);
      const hdr = normalize(exp.header);

      // Try direct lookups
      let acc = namePatterns.get(sub);
      if (!acc) {
        // Try matching when subheader starts with code- or ends with -name
        // Example: "345-salary to staff" → match by code and name
        const parts = sub.split("-");
        if (parts.length >= 2) {
          const possibleName = parts.slice(1).join("-");
          acc = namePatterns.get(possibleName) || namePatterns.get(`-${possibleName}`);
        }
      }
      if (!acc) return false;

      // Require header to match account.type
      if (hdr !== normalize(acc.type)) return false;

      return true;
    });

    // Debug counts
    try {
      // eslint-disable-next-line no-console
      console.log("[TDS] filteredAccounts", filteredAccounts.length, "expensesInRange", relevantExpenses.length);
    } catch (e) { }

    const group = new Map();
    relevantExpenses.forEach((exp) => {
      const sub = normalize(exp.subheader);
      let acc = namePatterns.get(sub);
      if (!acc) {
        const parts = sub.split("-");
        if (parts.length >= 2) {
          const possibleName = parts.slice(1).join("-");
          acc = namePatterns.get(possibleName) || namePatterns.get(`-${possibleName}`);
        }
      }
      if (!acc) return;
      const key = String(acc._id);
      const prev = group.get(key) || { account: acc, expenseTotal: 0, expenseCount: 0 };
      prev.expenseTotal += Number(exp.amount || 0);
      prev.expenseCount += 1;
      group.set(key, prev);
    });

    const rows = Array.from(group.values()).map(({ account, expenseTotal, expenseCount }) => {
      const taxRate = Number(account.taxRate || 0);
      const tdsAmount = +(expenseTotal * (taxRate / 100)).toFixed(2);
      return {
        accountName: account.name,
        accountType: account.type,
        taxRate,
        expenseCount,
        expenseTotal: +expenseTotal.toFixed(2),
        tdsAmount,
      };
    });
    rows.sort((a, b) => b.tdsAmount - a.tdsAmount);

    const totals = rows.reduce(
      (acc, r) => {
        acc.expenseTotal += r.expenseTotal;
        acc.tdsAmount += r.tdsAmount;
        acc.expenseCount += r.expenseCount;
        return acc;
      },
      { expenseTotal: 0, tdsAmount: 0, expenseCount: 0 }
    );

    setTdsRows(rows);
    setTdsTotals(totals);
  };

  return (
    <Box className="w-full min-h-screen bg-gray-50 pt-20 pb-8 px-2 md:px-8">
      <Paper
        elevation={3}
        className="w-full max-w-[98vw] md:max-w-[90vw] lg:max-w-[1400px] mx-auto p-4 md:p-8 rounded-3xl shadow-lg"
        sx={{
          minHeight: "80vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="h4"
          fontWeight={700}
          gutterBottom
          className="mb-2 text-blue-900"
        >
          Deductions & TDS
        </Typography>

        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ mb: 2 }}
        >
          <Tab label="Deduction" />
          <Tab label="TDS" />
        </Tabs>

        <Divider sx={{ mb: 2 }} />

        {tabIndex === 0 && (
          <>
            <Typography variant="h6" fontWeight={600} className="mb-4">
              Deduction Details
            </Typography>
            <Box className="flex flex-col md:flex-row gap-4 items-center mb-6 w-full">
              <FormControl sx={{ minWidth: 260 }} className="flex-1">
                <InputLabel>Deduction Name</InputLabel>
                <Select
                  value={selectedDeduction}
                  label="Deduction Name"
                  onChange={(e) => setSelectedDeduction(e.target.value)}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  {allDeductionNames.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Month"
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }}
                className="flex-1"
              />
              <Button
                variant="contained"
                color="primary"
                sx={{ height: 56, fontWeight: 600, px: 4 }}
                className="w-full md:w-auto"
                onClick={handleUpdate}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "Update"}
              </Button>
            </Box>
            {error && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TableContainer
              component={Paper}
              className="rounded-2xl shadow-md"
              sx={{
                mt: 2,
                maxHeight: 600,
                overflowY: "auto",
                width: "100%",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className="font-bold text-lg bg-blue-50">
                      Employee
                    </TableCell>
                    <TableCell className="font-bold text-lg bg-blue-50">
                      Email
                    </TableCell>
                    <TableCell className="font-bold text-lg bg-blue-50 text-right">
                      Deducted Amount
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.length > 0 ? (
                    <>
                      {result.map((row, idx) => (
                        <TableRow key={idx} className="hover:bg-blue-50 transition">
                          <TableCell>{row.employee}</TableCell>
                          <TableCell>{row.email}</TableCell>
                          <TableCell align="right">
                            <Typography color="error" fontWeight={600}>
                              {row.amount}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={2} align="right" className="font-bold text-lg">
                          Total
                        </TableCell>
                        <TableCell align="right" className="font-bold text-lg text-blue-900">
                          {totalDeduction}
                        </TableCell>
                      </TableRow>
                    </>
                  ) : (
                    !loading && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No data to display.
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box className="flex justify-end mt-8 w-full">
              <Button
                variant={isPaid ? "outlined" : "contained"}
                color={isPaid ? "success" : "primary"}
                disabled={isPaid || !result.length || payLoading}
                onClick={handleProceedToPay}
                sx={{
                  minWidth: 260,
                  fontWeight: 700,
                  fontSize: 18,
                  py: 1.8,
                  borderRadius: 2,
                  boxShadow: 2,
                  bgcolor: isPaid ? "#e3fcec" : undefined,
                  color: isPaid ? "#2e7d32" : undefined,
                }}
                className="w-full md:w-auto"
              >
                {payLoading ? (
                  <Box className="flex items-center justify-center w-full">
                    <CircularProgress size={28} color="inherit" />
                    <span className="ml-3 text-base font-semibold">Processing...</span>
                  </Box>
                ) : isPaid ? (
                  "Paid"
                ) : (
                  "Proceed to Pay to Organization"
                )}
              </Button>
            </Box>
          </>
        )}

        {tabIndex === 1 && (
          <>
            <Typography variant="h6" fontWeight={600} className="mb-4">
              TDS Summary
            </Typography>
            {tdsError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {tdsError}
              </Alert>
            )}
            <Box className="flex flex-col md:flex-row gap-4 items-center mb-4 w-full">
              <FormControl sx={{ minWidth: 260 }} className="flex-1">
                <InputLabel>Account</InputLabel>
                <Select
                  label="Account"
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                >
                  <MenuItem value="all">All taxable accounts</MenuItem>
                  {taxableAccounts.map((a) => (
                    <MenuItem key={a._id} value={a._id}>{`${a.name} (${a.taxRate}%)`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Start date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 200 }}
                className="flex-1"
              />
              <TextField
                label="End date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 200 }}
                className="flex-1"
              />
              <Button
                variant="contained"
                color="primary"
                sx={{ height: 56, fontWeight: 600, px: 4 }}
                className="w-full md:w-auto"
                onClick={handleTdsUpdate}
                disabled={tdsLoading}
              >
                {tdsLoading ? <CircularProgress size={24} /> : "Update"}
              </Button>
            </Box>

            <Box className="flex items-center gap-2 mb-2">
              <Chip label={`Accounts: ${taxableAccounts.length}`} />
              <Chip label={`Rows: ${tdsRows.length}`} />
              <Chip label={`Expenses: ${tdsTotals.expenseCount}`} />
              <Chip color="primary" label={`TDS Total: ${tdsTotals.tdsAmount.toFixed(2)}`} />
            </Box>

            <TableContainer
              component={Paper}
              className="rounded-2xl shadow-md"
              sx={{
                mt: 2,
                maxHeight: 600,
                overflowY: "auto",
                width: "100%",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className="font-bold bg-blue-50">Account</TableCell>
                    <TableCell className="font-bold bg-blue-50">Type</TableCell>
                    <TableCell className="font-bold bg-blue-50" align="right">Tax Rate %</TableCell>
                    <TableCell className="font-bold bg-blue-50" align="right">Expenses</TableCell>
                    <TableCell className="font-bold bg-blue-50" align="right">Expense Total</TableCell>
                    <TableCell className="font-bold bg-blue-50" align="right">TDS Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tdsLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : tdsRows.length > 0 ? (
                    <>
                      {tdsRows.map((r, idx) => (
                        <TableRow key={idx} className="hover:bg-blue-50 transition">
                          <TableCell>{r.accountName}</TableCell>
                          <TableCell>{r.accountType}</TableCell>
                          <TableCell align="right">{r.taxRate}</TableCell>
                          <TableCell align="right">{r.expenseCount}</TableCell>
                          <TableCell align="right">{r.expenseTotal.toFixed(2)}</TableCell>
                          <TableCell align="right">
                            <Typography color="primary" fontWeight={700}>
                              {r.tdsAmount.toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={3} align="right" className="font-bold">
                          Totals
                        </TableCell>
                        <TableCell align="right" className="font-bold">{tdsTotals.expenseCount}</TableCell>
                        <TableCell align="right" className="font-bold">{tdsTotals.expenseTotal.toFixed(2)}</TableCell>
                        <TableCell align="right" className="font-bold text-blue-900">{tdsTotals.tdsAmount.toFixed(2)}</TableCell>
                      </TableRow>
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">No data to display.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default DeductionDetails;